import {
  TellescopeModelFromName,
  useAuth,
  useTellescopeInfiniteQuery,
  useTellescopeMutation,
  useTellescopeQueries,
  useTellescopeQuery,
} from "@tellescope/tanstack-query";
import { BaseTicketActionProps } from "./types";
import { MessageCircle } from "lucide-react";
import { Button, Combobox, ComboboxOption, Input, Label, ReversePaginatedList, Textarea } from "@tellescope/ui";
import { Spinner } from "@tellescope/design-system";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { MessageBubbleWithSecureAvatar } from "@tellescope/messaging";
import { formatName } from "@tellescope/utils";
import { useForm } from "@tellescope/forms";

interface InboxChatRecipientItemProps {
  enduser?: TellescopeModelFromName<"endusers">;
}

const InboxChatRecipientItem = ({ enduser }: InboxChatRecipientItemProps) => {
  return (
    <div className="flex gap-2 items-center">
      <div className="flex flex-col">{!!enduser && <p className="font-semibold">{formatName(enduser)}</p>}</div>
    </div>
  );
};

export interface SendChatActionProps extends BaseTicketActionProps<"Send Chat"> {
  onSend: () => void;
}

export const SendChatAction = ({ completedAt, info, onSend, enduserId }: SendChatActionProps) => {
  const { t } = useTranslation("tickets");
  const templateQuery = useTellescopeQuery({
    model: "templates",
    method: "getOne",
    request: info.templateId,
    enabled: !!info.templateId && !completedAt,
  });

  const chatQuery = useTellescopeQuery({
    model: "chats",
    method: "getOne",
    request: info.chatId ?? "",
    enabled: !!info.chatId && !!completedAt,
  });

  const threadQuery = useTellescopeInfiniteQuery({
    model: "chats",
    method: "getSome",
    request: {
      to: chatQuery.data?.createdAt ? new Date(chatQuery.data?.createdAt.getTime() + 1000) : undefined,
      filter: {
        roomId: info.chatRoomId,
      },
      limit: 25,
    },
    enabled: !!info.chatRoomId && !!info.chatId && !!completedAt,
    select: (data) => data.pages.flat().reverse(),
  });

  // Get unique user and enduser IDs from messages
  const { userIds, enduserIds } = useMemo(() => {
    const messages = threadQuery.data?.flat() ?? [];
    const userIds = new Set<string>();
    const enduserIds = new Set<string>();

    messages.forEach((msg) => {
      if (msg.userId) userIds.add(msg.userId);
      if (msg.enduserId) enduserIds.add(msg.enduserId);
    });

    return {
      userIds: Array.from(userIds),
      enduserIds: Array.from(enduserIds),
    };
  }, [threadQuery.data]);

  const usersMap = useTellescopeQueries({
    model: "users",
    method: "getOne",
    requests: userIds,
    combine: (queries) =>
      queries.reduce<Record<string, TellescopeModelFromName<"users">>>((acc, curr) => {
        if (curr.data) acc[curr.data.id] = curr.data;
        return acc;
      }, {}),
    enabled: userIds.length > 0,
  });

  // Query endusers
  const endusersMap = useTellescopeQueries({
    model: "endusers",
    method: "getOne",
    requests: enduserIds,
    combine: (queries) =>
      queries.reduce<Record<string, TellescopeModelFromName<"endusers">>>((acc, curr) => {
        if (curr.data) acc[curr.data.id] = curr.data;
        return acc;
      }, {}),
    enabled: enduserIds.length > 0,
  });

  // Sending UI for non-completed actions
  const { currentUser } = useAuth();
  const [enduserSearch, setEnduserSearch] = useState("");

  const endusersQuery = useTellescopeInfiniteQuery({
    model: "endusers",
    method: "getSome",
    request: {
      limit: 20,
      search: enduserSearch
        ? {
            query: enduserSearch,
          }
        : undefined,
    },
    select: (data) => data.pages.flat(),
    enabled: !completedAt,
  });

  const createRoomMutation = useTellescopeMutation({
    model: "chat_rooms",
    method: "createOne",
  });

  const sendMessageMutation = useTellescopeMutation({
    model: "chats",
    method: "createOne",
  });

  const form = useForm({
    fields: {
      enduserId: {
        type: "text",
        initialValue: enduserId ?? "",
        required: true,
      },
      subject: {
        type: "text",
        initialValue: "",
        required: true,
      },
      message: {
        type: "textarea",
        initialValue: templateQuery.data?.message ?? "",
        required: true,
      },
    },
    validators: {
      message: (value) => (value.length > 0 ? undefined : t("Message cannot be empty")),
    },
    validateOn: "change",
    onSubmit: async (values) => {
      if (!values.enduserId || !values.subject || !currentUser.id) return;

      try {
        // Step 1: Create chat room
        const createdRoom = await createRoomMutation.mutateAsync({
          title: values.subject,
          enduserIds: [values.enduserId],
        });

        // Step 2: Send initial message
        await sendMessageMutation.mutateAsync({
          roomId: createdRoom.id,
          message: values.message,
          userId: currentUser.id,
          attachments: [],
          linkOpenTrackingIds: [],
        });

        // Step 3: Complete action
        onSend();
      } catch (error) {
        console.error("Failed to send chat message:", error);
      }
    },
  });

  // Pre-load enduser if enduserId is provided
  const selectedEnduserQuery = useTellescopeQuery({
    model: "endusers",
    method: "getOne",
    request: form.values.enduserId,
    enabled: !!form.values.enduserId && !completedAt,
  });

  if (completedAt) {
    return (
      <div className="space-y-2 p-4 border border-gray-200 rounded-lg bg-white">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 flex-1 min-w-0">
            <MessageCircle className="h-4 w-4 text-gray-600 flex-shrink-0" />
            <h3 className="text-sm font-medium text-gray-900 truncate">{t("Chat Conversation")}</h3>
          </div>
        </div>

        {threadQuery.data ? (
          <ReversePaginatedList
            className="border border-gray-200 rounded-lg max-h-[400px] p-4"
            onTopReached={() => threadQuery.fetchNextPage()}
          >
            {threadQuery.data.map((message) => {
              const user = message.senderId ? usersMap[message.senderId] : undefined;
              const enduser = message.senderId ? endusersMap[message.senderId] : undefined;

              const isOutbound = !!user && !enduser;
              const scheduledTime = message.sendAt
                ? message.sendAt.getTime() > new Date().getTime()
                  ? message.sendAt
                  : undefined
                : undefined;

              const sender = message.attachments ? endusersMap[message.creator] : usersMap[message.creator];

              return (
                <MessageBubbleWithSecureAvatar
                  key={message.id}
                  content={message.message}
                  timestamp={scheduledTime ?? message.timestamp ?? message.createdAt}
                  isOutbound={isOutbound}
                  sender={
                    sender
                      ? {
                          id: sender.id,
                          type: user ? "user" : "enduser",
                          name: formatName(sender),
                          avatar: sender.avatar,
                        }
                      : {
                          id: "",
                          type: "user",
                          name: t("Unknown"),
                        }
                  }
                  status={scheduledTime ? { type: "scheduled" } : undefined}
                />
              );
            })}
          </ReversePaginatedList>
        ) : (
          <div className="w-full flex items-center justify-center">
            <Spinner />
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-4 border border-gray-200 rounded-lg bg-white">
      <div className="flex items-center justify-between bg-gray-100 p-4 border-b border-b-gray-200">
        <div className="flex items-center gap-2 flex-1 min-w-0">
          <MessageCircle className="h-4 w-4 text-gray-600 flex-shrink-0" />
          <h3 className="text-sm font-medium text-gray-900 truncate">{t("Send Chat")}</h3>
        </div>
      </div>

      <form className="space-y-4 p-4" {...form.getFormProps()}>
        <div className="space-y-2">
          <Label htmlFor="recipient">{t("To")}</Label>
          <Combobox
            value={{
              value: form.getFieldProps("enduserId").value,
              label: selectedEnduserQuery.data ? formatName(selectedEnduserQuery.data) : "",
              metadata: { enduserId: form.getFieldProps("enduserId").value },
            }}
            formatSelectedValue={({ label }) => <p>{label}</p>}
            onChange={(newVal) => {
              if (typeof newVal?.metadata?.enduserId === "string") {
                form.setFieldValue("enduserId", newVal?.metadata?.enduserId);
              } else {
                form.setFieldValue("enduserId", "");
              }
            }}
            formatOption={(props) => (
              <InboxChatRecipientItem
                enduser={endusersQuery.data?.find((enduser) => enduser.id === props.option.metadata?.enduserId)}
              />
            )}
            searchQuery={enduserSearch}
            setSearchQuery={setEnduserSearch}
            customFilter={(_, options) => options}
            placeholder={t("Select recipient...")}
            options={
              endusersQuery.data?.map<ComboboxOption<string>>((enduser) => ({
                value: enduser.id,
                label: formatName(enduser),
                metadata: {
                  enduserId: enduser.id,
                },
              })) ?? []
            }
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="subject">{t("Subject")}</Label>
          <Input
            id="subject"
            type="text"
            placeholder={t("Enter subject")}
            {...form.getFieldProps("subject")}
            className="h-9"
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="message">{t("Message")}</Label>
          <Textarea
            id="message"
            placeholder={t("Type your message...")}
            {...form.getFieldProps("message")}
            className="min-h-[120px] resize-none"
          />
        </div>

        <div className="flex justify-end">
          <Button type="submit" disabled={!form.isValid || form.isSubmitting}>
            {form.isSubmitting ? t("Sending...") : t("Send Chat")}
          </Button>
        </div>
      </form>
    </div>
  );
};
